import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  MoreVertical,
  Loader2,
  ArrowLeft,
  FileText,
  Globe2,
  SlidersHorizontal,
  UploadCloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  listKnowledgeSourcesServer,
  deleteKnowledgeSourceServer,
  resyncKnowledgeSourceServer,
  getKnowledgeSourceServer,
  type KnowledgeSource,
  type SourceListResponse,
  type SourceDetail,
} from "@/server/knowledge-sources";
import { CreatePdfDialog } from "@/components/knowledge/create-pdf-dialog";
import { CreateTextDialog } from "@/components/knowledge/create-text-dialog";
import { ChatExtractBlock } from "@/components/knowledge/chat-extract-block";
import { MarkdownContentPreview } from "@/components/knowledge/markdown-content-preview";
import {
  knowledgeDialogBodyClass,
  knowledgeDialogCancelButtonClass,
  knowledgeDialogContentClass,
  knowledgeDialogDangerButtonClass,
  knowledgeDialogFooterClass,
  knowledgeDialogHeaderClass,
  knowledgeDialogTitleClass,
} from "@/components/knowledge/dialog-styles";
import { cn } from "@/lib/utils";

type SourcesTab = "all" | "snippet" | "document" | "webpage";
interface SourcesSearchParams {
  tab?: SourcesTab;
  sourceId?: string;
}

export const Route = createFileRoute("/workspace/$workspaceSlug/knowledge/sources")({
  validateSearch: (search: Record<string, unknown>): SourcesSearchParams => {
    const rawTab = typeof search.tab === "string" ? search.tab : undefined;
    const tab = typeof rawTab === "string" ? (rawTab as SourcesTab) : undefined;
    const isValidTab =
      tab === "all" || tab === "snippet" || tab === "document" || tab === "webpage";

    return {
      tab: isValidTab ? tab : undefined,
      sourceId: typeof search.sourceId === "string" ? search.sourceId : undefined,
    };
  },
  loader: async ({ parentMatchPromise }) => {
    const parent = await parentMatchPromise;
    const workspaceId = parent?.loaderData?.workspaceId;

    if (!workspaceId) {
      throw new Error("workspaceId not found in parent loader");
    }

    return { workspaceId };
  },
  component: KnowledgeSourcesPage,
});

type SourceType = SourcesTab;
type DialogType = "none" | "document" | "text";

const SOURCE_TYPE_FILTERS: Array<{ key: SourceType; labelKey: string; color: string }> = [
  { key: "all", labelKey: "knowledge:tabs.all", color: "bg-slate-500" },
  { key: "snippet", labelKey: "knowledge:tabs.snippets", color: "bg-violet-500" },
  { key: "document", labelKey: "knowledge:tabs.documents", color: "bg-blue-500" },
  { key: "webpage", labelKey: "knowledge:tabs.webpages", color: "bg-emerald-500" },
];

const typeLabels = (translate: (key: string) => string) => ({
  snippet: translate("knowledge:types.snippet"),
  document: translate("knowledge:types.document"),
  webpage: translate("knowledge:types.webpage"),
});

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  deleted: "bg-gray-100 text-gray-800",
};

function isResyncable(source: KnowledgeSource): boolean {
  if (source.source_type !== "webpage") return false;
  const importSource = source.import_source as
    | { root_url?: unknown; source_url?: unknown }
    | null
    | undefined;
  if (!importSource) return false;
  return typeof importSource.root_url === "string" || typeof importSource.source_url === "string";
}

interface KnowledgeSourceActionButtonProps {
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

function KnowledgeSourceActionButton({
  icon,
  title,
  description,
  onClick,
}: KnowledgeSourceActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:border-gray-300 hover:bg-gray-50"
    >
      <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
        {icon}
      </span>
      <span className="block text-sm font-semibold text-gray-950">{title}</span>
      <span className="mt-2 block text-sm leading-5 text-gray-500">{description}</span>
    </button>
  );
}

function KnowledgeSourcesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const { workspaceId } = Route.useLoaderData();
  const search = Route.useSearch();
  const [activeTab, setActiveTab] = useState<SourceType>(search.tab ?? "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogType, setDialogType] = useState<DialogType>("none");
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeSource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewSourceId, setViewSourceId] = useState<string | null>(search.sourceId ?? null);

  useEffect(() => {
    if (!search.tab) return;
    if (search.tab === activeTab) return;
    setActiveTab(search.tab);
  }, [activeTab, search.tab]);

  useEffect(() => {
    setViewSourceId(search.sourceId ?? null);
  }, [search.sourceId]);

  const { data, isLoading, error } = useQuery<SourceListResponse>({
    queryKey: ["knowledge-sources", workspaceId, activeTab],
    queryFn: async () => {
      const result = await listKnowledgeSourcesServer({
        data: {
          workspaceId,
          sourceType: activeTab === "all" ? undefined : activeTab,
          page: 1,
          pageSize: 50,
        },
      });
      return result as SourceListResponse;
    },
  });

  const sources: KnowledgeSource[] = data?.items ?? [];
  const filteredSources = sources.filter((source) =>
    source.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const { data: selectedSource, isLoading: isDetailLoading } = useQuery<SourceDetail>({
    queryKey: ["knowledge-source-detail", workspaceId, viewSourceId],
    enabled: !!viewSourceId,
    queryFn: async () => {
      if (!viewSourceId) {
        throw new Error(t("knowledge:errors.missingSourceId"));
      }
      const result = await getKnowledgeSourceServer({
        data: {
          workspaceId,
          sourceId: viewSourceId,
        },
      });
      return result as SourceDetail;
    },
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await deleteKnowledgeSourceServer({
        data: {
          workspaceId,
          sourceId: deleteTarget.id,
        },
      });
      toast.success(t("knowledge:toast.deleteSuccess"));
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("knowledge:toast.deleteFailed"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleResync = async (source: KnowledgeSource) => {
    try {
      await resyncKnowledgeSourceServer({
        data: {
          workspaceId,
          sourceId: source.id,
        },
      });
      toast.success(t("knowledge:toast.resyncSuccess"));
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("knowledge:toast.resyncFailed"));
    }
  };

  const handleTabChange = (nextTab: SourceType) => {
    setActiveTab(nextTab);
    void navigate({
      search: {
        tab: nextTab === "all" ? undefined : nextTab,
        sourceId: undefined,
      },
    });
  };

  const openDocumentDialog = () => {
    setSelectionDialogOpen(false);
    setDialogType("document");
  };

  const openTextDialog = () => {
    setSelectionDialogOpen(false);
    setDialogType("text");
  };

  const closeSourceSheet = () => {
    setViewSourceId(null);
    void navigate({
      search: {
        tab: activeTab === "all" ? undefined : activeTab,
        sourceId: undefined,
      },
    });
  };

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6">
        <p className="text-red-500">{t("knowledge:errors.loadFailed")}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] })}
        >
          {t("common:retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-white [font-family:inherit]">
      <div className="px-6 pt-4 pb-1">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-[460px] max-w-[min(460px,48vw)] items-center gap-3 rounded-full border border-transparent bg-muted/60 px-3 transition-colors focus-within:border-blue-500">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <Input
                  placeholder={t("knowledge:search.placeholder")}
                  className="h-8 min-w-0 border-0 bg-transparent px-0 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
              <Tooltip>
                <DropdownMenu>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0 rounded-full bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/15"
                        aria-label={t("pages:common.tooltips.showSearchOptions")}
                      >
                        <SlidersHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={10}
                    className="w-56 rounded-xl border-slate-200 p-2 shadow-[0_10px_28px_rgba(15,23,42,0.16)]"
                  >
                    {SOURCE_TYPE_FILTERS.map((option) => {
                      const isSelected = activeTab === option.key;

                      return (
                        <DropdownMenuItem
                          key={option.key}
                          className="flex h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-[15px] text-slate-950 focus:bg-slate-100"
                          onSelect={() => handleTabChange(option.key)}
                        >
                          <span className="flex size-5 shrink-0 items-center justify-center">
                            {isSelected ? (
                              <span className="h-2.5 w-4 rotate-[-45deg] border-b-2 border-l-2 border-slate-950" />
                            ) : null}
                          </span>
                          <span className={cn("size-2.5 shrink-0 rounded-full", option.color)} />
                          <span className="min-w-0 truncate">{t(option.labelKey)}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <TooltipContent side="bottom" align="end">
                  {t("pages:common.tooltips.showSearchOptions")}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <Button className="shrink-0" onClick={() => setSelectionDialogOpen(true)}>
            {t("knowledge:addContent.title")}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 pt-2 pb-4">
        {/* Table */}
        {isLoading ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : sources.length === 0 ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-6 py-10">
            <div className="w-full max-w-[820px]">
              <div className="mb-7 text-center">
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-gray-950 text-white">
                  <FileText className="h-5 w-5" />
                </div>
                <h2 className="text-[20px] font-semibold text-gray-950">
                  {t("knowledge:empty.title")}
                </h2>
                <p className="mx-auto mt-3 max-w-[560px] text-sm leading-6 text-gray-500">
                  {t("knowledge:empty.description")}
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <KnowledgeSourceActionButton
                  icon={<Globe2 className="h-4 w-4" />}
                  title={t("knowledge:empty.webpage.title")}
                  description={t("knowledge:empty.webpage.description")}
                  onClick={() => setSelectionDialogOpen(true)}
                />
                <KnowledgeSourceActionButton
                  icon={<UploadCloud className="h-4 w-4" />}
                  title={t("knowledge:empty.document.title")}
                  description={t("knowledge:empty.document.description")}
                  onClick={openDocumentDialog}
                />
                <KnowledgeSourceActionButton
                  icon={<FileText className="h-4 w-4" />}
                  title={t("knowledge:empty.text.title")}
                  description={t("knowledge:empty.text.description")}
                  onClick={openTextDialog}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-auto rounded-lg border border-gray-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead>{t("knowledge:table.name")}</TableHead>
                  <TableHead>{t("knowledge:table.type")}</TableHead>
                  <TableHead>{t("knowledge:table.status")}</TableHead>
                  <TableHead>{t("knowledge:table.lastUpdated")}</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-gray-500">
                      {t("knowledge:empty.noSearchResults")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSources.map((source: KnowledgeSource) => {
                    return (
                      <TableRow key={source.id} className="cursor-pointer hover:bg-gray-50">
                        <TableCell className="font-medium text-gray-900">
                          <div className="flex items-center gap-2">{source.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{typeLabels(t)[source.source_type]}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                              statusColors[source.status]
                            }`}
                          >
                            {t(`knowledge:status.${source.status}`)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {format(new Date(source.updated_at), "yyyy-MM-dd HH:mm")}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {source.source_type === "webpage" && isResyncable(source) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResync(source);
                                  }}
                                >
                                  {t("knowledge:actions.resync")}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-red-600 focus:bg-red-50 focus:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(source);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                {t("knowledge:actions.delete")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Content Selection Dialog */}
      <Dialog open={selectionDialogOpen} onOpenChange={setSelectionDialogOpen}>
        <DialogContent
          className={`w-[640px] max-w-[calc(100vw-32px)] overflow-visible ${knowledgeDialogContentClass} sm:max-w-[640px]`}
        >
          <DialogHeader className={knowledgeDialogHeaderClass}>
            <DialogTitle className={knowledgeDialogTitleClass}>
              {t("knowledge:addContent.dialogTitle")}
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-6 text-gray-500">
              {t("knowledge:addContent.description")}
            </DialogDescription>
          </DialogHeader>
          <div className={`${knowledgeDialogBodyClass} pb-8`}>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <ChatExtractBlock
                workspaceId={workspaceId}
                onSuccess={() => {
                  setSelectionDialogOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
                }}
              />
            </div>

            <p className="mt-5 text-sm font-medium text-gray-950">
              {t("knowledge:addContent.orStartManually")}
            </p>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <KnowledgeSourceActionButton
                icon={<UploadCloud className="h-4 w-4" />}
                title={t("knowledge:addContent.document.title")}
                description={t("knowledge:addContent.document.description")}
                onClick={openDocumentDialog}
              />
              <KnowledgeSourceActionButton
                icon={<FileText className="h-4 w-4" />}
                title={t("knowledge:addContent.text.title")}
                description={t("knowledge:addContent.text.description")}
                onClick={openTextDialog}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialogs */}
      <CreatePdfDialog
        open={dialogType === "document"}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDialogType("none");
          }
        }}
        onBack={() => {
          setDialogType("none");
          setSelectionDialogOpen(true);
        }}
        workspaceId={workspaceId}
        onSuccess={() => {
          setDialogType("none");
          queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
        }}
      />

      <CreateTextDialog
        open={dialogType === "text"}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDialogType("none");
          }
        }}
        onBack={() => {
          setDialogType("none");
          setSelectionDialogOpen(true);
        }}
        workspaceId={workspaceId}
        onSuccess={() => {
          setDialogType("none");
          queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
        }}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className={`${knowledgeDialogContentClass} sm:max-w-[520px]`}>
          <DialogHeader className={knowledgeDialogHeaderClass}>
            <DialogTitle className={knowledgeDialogTitleClass}>
              {t("knowledge:deleteDialog.title")}
            </DialogTitle>
            <DialogDescription className="pt-4 text-base text-gray-500">
              {t("knowledge:deleteDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={knowledgeDialogFooterClass}>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              className={knowledgeDialogCancelButtonClass}
            >
              {t("knowledge:deleteDialog.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className={knowledgeDialogDangerButtonClass}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("knowledge:deleteDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet
        open={viewSourceId !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeSourceSheet();
          }
        }}
      >
        <SheetContent side="right" className="sm:max-w-xl w-full p-0 gap-0">
          <SheetTitle className="sr-only">
            {selectedSource?.name ?? t("knowledge:sourceDetail.title")}
          </SheetTitle>
          <div className="border-b px-6 py-5">
            <div className="flex items-center gap-2 text-lg font-normal">
              <ArrowLeft
                className="h-5 w-5 cursor-pointer hover:opacity-70"
                onClick={closeSourceSheet}
              />
              {selectedSource?.name ?? t("knowledge:sourceDetail.title")}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isDetailLoading || !selectedSource ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-normal">{t("knowledge:sourceDetail.name")}</Label>
                  <Input
                    value={selectedSource.name}
                    readOnly
                    className="border-2 border-gray-300 rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-normal">{t("knowledge:sourceDetail.tags")}</Label>
                  <div className="border-2 border-gray-300 rounded-lg p-3 min-h-[48px]">
                    {selectedSource.tags.length === 0 ? (
                      <div className="text-sm text-gray-500">
                        {t("knowledge:sourceDetail.noTags")}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedSource.tags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="bg-gray-100 text-gray-800 border-0 rounded-full px-3 py-1"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-normal">
                    {t("knowledge:sourceDetail.content")}
                  </Label>
                  {selectedSource.documents.length === 0 ? (
                    <div className="text-sm text-gray-500">
                      {t("knowledge:sourceDetail.noContent")}
                    </div>
                  ) : selectedSource.documents.length === 1 ? (
                    <MarkdownContentPreview
                      content={selectedSource.documents[0]?.content ?? ""}
                      emptyText={t("knowledge:sourceDetail.noContent")}
                    />
                  ) : (
                    <div className="space-y-3">
                      {selectedSource.documents.map((doc, index) => (
                        <div key={doc.id} className="space-y-2">
                          <div className="text-xs text-gray-500">
                            {doc.title || doc.page_number
                              ? `${t("knowledge:sourceDetail.page")} ${doc.page_number}`
                              : `Item ${index + 1}`}
                          </div>
                          <MarkdownContentPreview
                            content={doc.content}
                            emptyText={t("knowledge:sourceDetail.noContent")}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t px-6 py-4 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={closeSourceSheet}
              className="rounded-full px-8 bg-gray-200 hover:bg-gray-300 text-gray-900"
            >
              {t("knowledge:sourceDetail.close")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
